import { Logo } from "@/components/site/Logo";

const Tile = ({ label, dark, children }) => (
  <div
    className={`p-8 md:p-10 flex flex-col justify-between min-h-[200px] ${
      dark ? "bg-[#111111] text-[#f9f9f7]" : "bg-[#f9f9f7] text-[#111111]"
    }`}
    data-testid={`brand-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
  >
    <div className="flex-1 flex items-center">{children}</div>
    <span className={`label mt-8 ${dark ? "text-[#777777]" : "text-[#555555]"}`}>{label}</span>
  </div>
);

const Row = ({ title, render }) => (
  <div className="mb-16">
    <p className="label text-[#555555] mb-4">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#e5e5e0] border rule">
      <Tile label="Colour on paper">{render({ mono: false })}</Tile>
      <Tile label="Mono black">{render({ mono: true })}</Tile>
      <Tile label="Reversed, colour" dark>{render({ mono: false })}</Tile>
      <Tile label="Reversed, mono" dark>{render({ mono: true })}</Tile>
    </div>
  </div>
);

export default function Brand() {
  return (
    <div className="min-h-screen bg-[#f9f9f7] text-[#111111]">
      <header className="border-b rule">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center" data-testid="brand-back-link">
            <Logo variant="wide" className="text-[15px]" />
          </a>
          <span className="label text-[#555555]">Brand system</span>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 md:px-12 py-20 md:py-28">
        <p className="label text-[#555555] mb-4">00 / Identity</p>
        <h1 className="font-light tracking-[-0.04em] leading-[0.95] text-4xl md:text-6xl mb-6">
          One mark. Every surface.
        </h1>
        <p className="text-sm text-[#555555] max-w-md leading-relaxed mb-16 md:mb-24">
          The dimension line measures the space between FRONT and AGE.
          Three formats, two tones, positive and reversed. Nothing else is permitted.
        </p>

        <Row title="A / Wide: primary" render={({ mono }) => <Logo variant="wide" mono={mono} className="text-2xl md:text-3xl" />} />
        <Row title="B / Compact: confined spaces" render={({ mono }) => <Logo variant="compact" mono={mono} className="text-xl md:text-2xl" />} />
        <Row title="C / Monogram: favicon, avatars, stamps" render={({ mono }) => <Logo variant="monogram" mono={mono} className="h-14 w-14" />} />

        <div className="mb-16">
          <p className="label text-[#555555] mb-4">D / Scale</p>
          <div className="border rule p-8 md:p-10 flex flex-col gap-8 items-start">
            <Logo variant="wide" className="text-4xl md:text-5xl" />
            <Logo variant="wide" className="text-2xl" />
            <Logo variant="wide" className="text-base" />
            <Logo variant="wide" className="text-[11px]" />
            <span className="label text-[#555555]">Minimum size: 60px wide. Below that, use the monogram.</span>
          </div>
        </div>

        <div>
          <p className="label text-[#555555] mb-4">E / Rules</p>
          <ul className="border-t rule max-w-2xl">
            {[
              "Clear space around the mark equals the height of the letter F.",
              "The orange is #FF4F00. It appears only in the dimension line, never in the letters.",
              "On photography or colour, use the reversed mono version.",
              "Do not stretch, outline, rotate or add effects to the mark.",
            ].map((rule) => (
              <li key={rule} className="border-b rule py-4 text-sm text-[#555555]">{rule}</li>
            ))}
          </ul>
        </div>
      </main>
    </div>
  );
}
